import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { rawApi } from '../../api/client';
import { errorCopy } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { formatDate, formatHours, fromIsoDate } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import type { StaffMember } from '../staff/PersonDetailsPanel';
import {
  DAY_SHORT,
  patternDayOn,
  SCHEDULES_PATH,
  type StaffSchedule,
  schedulesByStaff,
} from '../staff/schedule';
import type { PatternDay } from '../staff/WeeklyPatternEditor';
import { DAY_ENTRIES_PATH, describeShift, type TimesheetDayEntry } from './DayEntryEditor';
import {
  BLANK_CELL,
  type Cell,
  type CellMode,
  cellKey,
  cellMinutes,
  cellOfRecord,
  cellOfSchedule,
  DAY_ENTRIES_BULK_PATH,
  type DayEntryPut,
  eachDay,
  entryOf,
  MOST_DAYS_IN_ONE_SAVE,
  normalizeClock,
  parseDraft,
  readCell,
  recordedMinutes,
  refusedEntry,
  type Sheet,
  sheetAfter,
  sheetMayWrite,
} from './periodSheet';
import { WORKING_WEEKS_TAB } from './WorkingWeeksPanel';

const MEMBERS_PATH = '/admin/v1/staff/members';
const PERIODS_PATH = '/admin/v1/payroll/periods';
const TIMESHEETS_PATH = '/admin/v1/payroll/timesheets';
const TIME_ENTRY_POLICY_PATH = '/admin/v1/payroll/time-entry-policy';

/** Where the whole period gets typed. The approval screen links here, and so does the tab bar. */
export const PERIOD_HOURS_TAB = '/payroll?tab=hours';

/** Where an approved sheet is reopened, which is the only door back into a period already signed off. */
const APPROVE_HOURS_TAB = '/payroll';

/** The same, landing on one period — so a link from the hours being approved opens that fortnight. */
export const periodHoursLink = (periodId: string) => `${PERIOD_HOURS_TAB}&period=${periodId}`;

interface Period {
  id: string;
  periodStart: string;
  periodEnd: string;
}

/** The date from which a hand-entered day must carry its clock times. Null means every day must. */
interface TimeEntryPolicy {
  timesRequiredFrom: string | null;
}

/**
 * As much of a built timesheet as this screen has to read: what the last rebuild made of each day,
 * and whether that sheet has been APPROVED.
 *
 * The same request the approval screen makes, under the same key, so reading it here costs nothing.
 * The status is read for the paragraph on `behindTheFigures` below: a rebuild leaves an approved
 * sheet exactly as it is, so days entered against one reach nobody's pay however many times the
 * button is pressed, and only the status can say so before the button is pressed.
 */
interface BuiltSheet {
  staffId: string;
  staffName?: string | null;
  status?: string;
  days?: { date: string; workedMinutes: number }[];
}

/**
 * What a rebuild answers. `preserved` names the people it deliberately left alone — an approval is
 * a decision about what somebody is owed, and re-counting it would erase that decision quietly.
 * Discarding this list is how a screen comes to say "the days entered here are in the figures now"
 * about days that reached nobody.
 */
interface BuildResult {
  preserved?: { staffId: string; staffName: string; timesheetId: string; reason: string }[];
}

/** One typed cell as it goes on the wire, with what it takes to find it again on the grid. */
interface PendingDay {
  entry: DayEntryPut;
  key: string;
  who: string;
  date: string;
}

/** Sunday = 0, matching `DAY_SHORT` and the weekday numbers a working week is stored under. */
function weekdayOf(date: string): number | null {
  return fromIsoDate(date)?.getDay() ?? null;
}

/**
 * THE TYPED SHEET SURVIVES LEAVING THIS SCREEN.
 *
 * A fortnight typed here lives in one browser tab and nowhere else until it is sent, and the tab
 * bar above unmounts this panel the moment another tab is clicked — so the sheet was being thrown
 * away by, among other things, the three links this screen offers itself: set the working weeks,
 * open somebody's record, approve the hours.
 *
 * Warning before each of those was the other option and it is the dishonest one. A screen that
 * offers a link and then argues with the reader for taking it is a screen that does not mean the
 * link; a dialog on every tab click is clicked through inside a week; and neither survives a
 * reload. Keeping the draft is the mechanism that matches what the owner believes is happening —
 * he typed it, so it is still there — and it leaves exactly one way to throw typed work away,
 * which is the one that says so on the button.
 *
 * `sessionStorage`, not `localStorage`: a draft belongs to the sitting it was typed in. It survives
 * navigation, a reload and the back button, it cannot be clobbered by a second tab open on the
 * portal, and it does not resurface a month later beside a period that has since been paid. The
 * cells are keyed by person and day, so a draft spanning two periods is not a hazard — a cell can
 * only ever be shown, saved or cleared on the period whose dates it falls in.
 */
const DRAFT_KEY = 'decostop.payroll.periodHours';

function restoreDraft(): Sheet {
  try {
    return parseDraft(window.sessionStorage.getItem(DRAFT_KEY));
  } catch {
    // A browser that refuses storage costs the draft a tab switch — which is where this screen was
    // before the draft existed. It must never cost a keystroke, so nothing here throws.
    return {};
  }
}

function keepDraft(sheet: Sheet): void {
  try {
    if (Object.keys(sheet).length === 0) window.sessionStorage.removeItem(DRAFT_KEY);
    else window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(sheet));
  } catch {
    // As above: storage is a convenience here, never a precondition.
  }
}

/**
 * THAT A DAY CARRIES A NOTE, SAID WHERE THE DAY IS.
 *
 * The note is the one thing on a hand-entered day that nothing can reconstruct — why it was entered
 * by hand at all. The bulk door now protects it: a sheet that never mentions the field leaves the
 * sentence alone. That keeps it from being deleted, and it does not keep it from being contradicted
 * by somebody who could not see it. A grid of two hundred boxes was the one screen where a day's
 * only explanation was invisible, so it is shown here, whole, on hover or focus.
 */
function NoteMark({ note }: { note: string }) {
  return (
    <Tooltip label={note} multiline maw={280} withArrow position="top-start">
      <Text size="xs" c="dimmed" fs="italic" lineClamp={1} tabIndex={0} style={{ cursor: 'help' }}>
        {note}
      </Text>
    </Tooltip>
  );
}

/**
 * ONE PERSON-DAY. Memoized, because the sheet is one object in the screen above it and a month for
 * five people is a hundred and fifty of these: without it every keystroke re-renders three hundred
 * boxes, and the owner types a fortnight in a screen that lags behind his hands.
 *
 * What the cell is worth and why it cannot be saved are worked out ONCE above and handed down as
 * plain values, rather than being read again here — one reading of a cell, so the figure under a
 * box, the total in the heading and what goes on the wire cannot disagree.
 */
const DayCell = memo(function DayCell({
  sheetKey,
  who,
  when,
  mode,
  cell,
  minutes,
  fault,
  typed,
  onFile,
  note,
  ghostStart,
  ghostEnd,
  refusedAs,
  onChange,
}: {
  sheetKey: string;
  /** For the box's own label, which is all a screen reader gets in a grid this size. */
  who: string;
  when: string;
  mode: CellMode;
  cell: Cell;
  /** What this cell is worth as it stands, or null while it is empty or refused. */
  minutes: number | null;
  /** Why it cannot be saved, in the words that fit under it. */
  fault: string | null;
  /** Whether the owner has typed here — a typed cell is the only kind that gets sent. */
  typed: boolean;
  /** What is already recorded, when the boxes above cannot show it themselves. */
  onFile: string | null;
  /** The sentence kept with the day already on file, if it carries one. */
  note: string | null;
  /** The shift their working week expects, shown as a suggestion and never as a record. */
  ghostStart: string;
  ghostEnd: string;
  /** The server's own words, when this is the cell a wholesale refusal named. */
  refusedAs: string | null;
  onChange: (key: string, next: Cell) => void;
}) {
  const problem = refusedAs ?? fault;

  /*
   * Whether the shift crossed midnight is read back off `describeShift` rather than worked out
   * again here. A bar's week is full of shifts that end at 02:00 and there is one reading of
   * "18:00 to 02:00" in this portal — the moment there are two, one of them is wrong on a Tuesday.
   */
  const overnight =
    mode === 'times' && minutes !== null && describeShift(cell.start, cell.end).endsWith('(+1 day)');

  const boxes =
    mode === 'hours' ? (
      <TextInput
        size="xs"
        w={78}
        aria-label={`${who}, ${when}, hours worked`}
        placeholder="8"
        value={cell.hours}
        error={problem !== null}
        onChange={(e) => onChange(sheetKey, { ...cell, hours: e.currentTarget.value })}
      />
    ) : (
      <Group gap={4} wrap="nowrap">
        <TextInput
          size="xs"
          w={64}
          inputMode="numeric"
          aria-label={`${who}, ${when}, clock in`}
          placeholder={ghostStart || '16:00'}
          value={cell.start}
          error={problem !== null}
          onChange={(e) => onChange(sheetKey, { ...cell, start: e.currentTarget.value })}
          // Tidied when the box is left, never as it is typed: rewriting "1" to "01:00" mid-word
          // would eat the next keystroke and the owner would be fighting the field all afternoon.
          onBlur={() => onChange(sheetKey, { ...cell, start: normalizeClock(cell.start) })}
        />
        <TextInput
          size="xs"
          w={64}
          inputMode="numeric"
          aria-label={`${who}, ${when}, clock out`}
          placeholder={ghostEnd || '00:00'}
          value={cell.end}
          error={problem !== null}
          onChange={(e) => onChange(sheetKey, { ...cell, end: e.currentTarget.value })}
          onBlur={() => onChange(sheetKey, { ...cell, end: normalizeClock(cell.end) })}
        />
      </Group>
    );

  return (
    <Stack gap={2}>
      {boxes}
      {/* One line, always the same height, so nothing on a thirty-one-row grid jumps as it is
          typed into. Its color is the whole legend: dimmed is what is already on file, colored
          is what this save would add. The unpaid break is named only where it is actually taken
          out of the figure beside it — hours entered without times carry no break, subtract none
          and send none, and saying otherwise beside a total is a lie about what will be paid. */}
      <Text
        size="xs"
        mih={16}
        lh={1.3}
        c={problem ? 'red.7' : typed ? 'teal.7' : 'dimmed'}
        fw={typed ? 600 : 400}
      >
        {problem ??
          (minutes !== null
            ? `${formatHours(minutes)}${mode === 'hours' ? ' · no times' : overnight ? ' · past midnight' : ''}${
                mode === 'times' && cell.breakMinutes > 0 ? ` · ${cell.breakMinutes}m break` : ''
              }`
            : (onFile ?? ''))}
      </Text>
      {note ? <NoteMark note={note} /> : null}
    </Stack>
  );
});

/**
 * THE DAY THE CLOCK TOOK OVER, set where it is needed rather than buried in a settings list.
 *
 * Which days may be written as hours alone is the owner's answer and not a constant: it is when
 * his staff came onto the clock, it moves as he brings them on, and no release of this software can
 * know it. Until he names it, no day can be entered without times — which is the safe way round,
 * and also means this sheet cannot offer the mode at all. So the one control that opens it sits on
 * the one screen that wants it, saying what it costs before it is set.
 */
function ClockTookOverModal({ policy, onClose }: { policy: TimeEntryPolicy; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [date, setDate] = useState<string | null>(policy.timesRequiredFrom);

  const save = useMutation({
    mutationFn: (timesRequiredFrom: string | null) =>
      rawApi<TimeEntryPolicy>('PUT', TIME_ENTRY_POLICY_PATH, { timesRequiredFrom }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: listKey(TIME_ENTRY_POLICY_PATH) });
      notifyDone(
        saved.timesRequiredFrom ? 'Saved' : 'Every day needs its times again',
        saved.timesRequiredFrom
          ? `Days before ${formatDate(saved.timesRequiredFrom)} can be entered as hours alone.`
          : 'No day can be entered without its clock times now.',
      );
      onClose();
    },
    onError: (err) => notifyError(err, 'set the day the clock took over'),
  });

  return (
    <Modal opened onClose={onClose} title="The day your staff came onto the clock">
      <Stack gap="md">
        <Text size="sm">
          Days from this one on are recorded as a clock-in and a clock-out. Days before it can be entered as
          hours alone, for the months worked when nobody was punching and the times are genuinely gone.
        </Text>
        <Text size="sm" c="dimmed">
          Hours entered without times cannot be split into day and night bands later, so night and rest-day
          premiums can never be worked out for them. That is why the times are the default and the way forward
          — this only covers what is already behind you.
        </Text>
        <Text size="sm" c="dimmed">
          Moving this day forward does not touch what is already recorded. Hours typed on a day it brings onto
          the clock stay on the sheet and say so, so they can be entered as times rather than lost.
        </Text>
        <DateInput
          label="Times required from"
          value={date}
          onChange={(v) => setDate(v || null)}
          placeholder="Pick the day"
          popoverProps={{ withinPortal: true }}
        />
        <Group justify="space-between">
          {policy.timesRequiredFrom ? (
            <Button variant="subtle" color="red" disabled={save.isPending} onClick={() => save.mutate(null)}>
              Every day needs its times
            </Button>
          ) : (
            <span />
          )}
          <Group gap="xs">
            <Button variant="default" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button loading={save.isPending} disabled={!date} onClick={() => save.mutate(date)}>
              Save
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * A WHOLE PAY PERIOD, ENTERED IN ONE SITTING.
 *
 * What this replaces, measured: fourteen days for one person took about a hundred and twelve
 * separate interactions — per day, open the entry dialog, open the date picker, pick the day, click
 * into the clock-in, type it, tab, type the clock-out, click Record — and the dialog unmounted
 * after every save, so nothing carried forward. Three people was over three hundred and forty. The
 * owner's own account of it was that he could only backfill one day at a time.
 *
 * ## TYPED WORK IS SACRED, and it is `sheetAfter` that makes it true
 *
 * Everything below adds to the sheet or reads it. The only two things that take a cell off it are a
 * discard the owner asked for and a save that came back 200 — and the save prunes the keys it sent
 * and no others, taken from the request itself rather than from state that has moved on since. See
 * `periodSheet.ts`; the rule is stated there because a rule restated on a screen is a rule with two
 * readings.
 *
 * ## Why the days go DOWN and the people go ACROSS
 *
 * The two dimensions are not alike. A period is fourteen to thirty-one days and this bar's whole
 * payroll is a handful of people, so days-down gives a long column that vertical scrolling was made
 * for, while people-across stays within a screen and every person's total can sit in a heading that
 * is still on screen while their column is being typed. Turned the other way it is thirty-one
 * columns of horizontal scroll, which hides both ends of the period at once and puts each person's
 * total off the right-hand edge — and the thing the owner is reading from, a diary or a memory of
 * the month, runs down the calendar rather than across the staff.
 *
 * ## What it will not do, and why that is not a gap
 *
 * It writes worked days. A paid day off already has a door on the approval screen, where a note and
 * a reason belong with it, and a day off is rare and deliberate in a way a shift is not — so a PTO
 * day shows here as what it is and is left alone rather than being one wrong keystroke from being
 * overwritten. That is enforced on the SEND path and not only in the render: see `sheetMayWrite`.
 * It does not remove a day either: taking one off is a delete, which is a different gesture from a
 * sheet that saves in one call, and it stays where it already is.
 */
export function PeriodHoursGrid() {
  const queryClient = useQueryClient();
  /*
   * Which period is open lives in the address, so the approval screen can link into the fortnight
   * somebody is standing in rather than into whichever one this screen would have picked.
   */
  const [params, setParams] = useSearchParams();
  const askedPeriod = params.get('period');
  const setAskedPeriod = (periodId: string | null) => {
    setParams(
      (previous) => {
        const merged = new URLSearchParams(previous);
        if (periodId) merged.set('period', periodId);
        else merged.delete('period');
        return merged;
      },
      { replace: true },
    );
    // Both of these are statements about the period being left. What is TYPED stays: its cells are
    // keyed by person and day, so nothing can leak into another fortnight, and throwing away an
    // afternoon's work over a mis-click on the period picker would be unforgivable.
    setRefused(null);
    setLastSaved(null);
  };

  /**
   * Everything typed and not yet saved. Never seeded from the record — see `periodSheet.ts` on
   * blanks — and never pruned except by `sheetAfter`, which is the whole of the rule.
   */
  const [sheet, changeSheet] = useReducer(sheetAfter, undefined, restoreDraft);
  /** Whether this sitting opened with typing already on the sheet, so the screen can say so. */
  const [carriedOver] = useState(() => Object.keys(sheet).length > 0);
  useEffect(() => {
    keepDraft(sheet);
  }, [sheet]);

  const [hoursOnly, setHoursOnly] = useState(false);
  const [settingTheDate, setSettingTheDate] = useState(false);
  /** The cell a wholesale refusal named, and the sentence to put under it. */
  const [refused, setRefused] = useState<{ key: string; who: string; date: string; copy: string } | null>(
    null,
  );
  const [lastSaved, setLastSaved] = useState<number | null>(null);

  const periods = useQuery({
    queryKey: listKey(PERIODS_PATH),
    queryFn: () => rawApi<Period[]>('GET', PERIODS_PATH),
  });

  /*
   * The fortnight being worked NOW, by the same reading the approval screen gives it: the period
   * today falls inside off the ISLAND's calendar, then the most recent one that has already ended,
   * then the newest row. A site with the next two periods set up in advance would otherwise open on
   * a fortnight nobody has worked yet.
   */
  const today = useIslandToday();
  const byStartDesc = [...(periods.data ?? [])].sort((a, b) => b.periodStart.localeCompare(a.periodStart));
  const covering = byStartDesc.find((p) => p.periodStart <= today && today <= p.periodEnd);
  const lastFinished = byStartDesc.find((p) => p.periodEnd < today);
  const periodId =
    (askedPeriod && byStartDesc.some((p) => p.id === askedPeriod) ? askedPeriod : null) ??
    covering?.id ??
    lastFinished?.id ??
    byStartDesc[0]?.id ??
    null;
  const period = periods.data?.find((p) => p.id === periodId) ?? null;

  const staff = useQuery({
    queryKey: listKey(MEMBERS_PATH),
    queryFn: () => rawApi<StaffMember[]>('GET', MEMBERS_PATH),
  });
  const schedules = useQuery({
    queryKey: listKey(SCHEDULES_PATH),
    queryFn: () => rawApi<StaffSchedule[]>('GET', SCHEDULES_PATH),
  });
  const policy = useQuery({
    queryKey: listKey(TIME_ENTRY_POLICY_PATH),
    queryFn: () => rawApi<TimeEntryPolicy>('GET', TIME_ENTRY_POLICY_PATH),
  });
  const entries = useQuery({
    queryKey: listKey(DAY_ENTRIES_PATH, { from: period?.periodStart ?? '', to: period?.periodEnd ?? '' }),
    queryFn: () =>
      rawApi<TimesheetDayEntry[]>(
        'GET',
        `${DAY_ENTRIES_PATH}?from=${period?.periodStart}&to=${period?.periodEnd}`,
      ),
    enabled: Boolean(period),
  });
  const built = useQuery({
    queryKey: listKey(TIMESHEETS_PATH, { payrollPeriodId: periodId ?? '' }),
    queryFn: () => rawApi<BuiltSheet[]>('GET', `${TIMESHEETS_PATH}?payrollPeriodId=${periodId}`),
    enabled: Boolean(periodId),
  });

  const dates = useMemo(() => (period ? eachDay(period.periodStart, period.periodEnd) : []), [period]);

  /*
   * Everybody whose employment touches this period, rather than everybody who works here today.
   * The whole point of the screen is catching up months already behind us, and the person whose
   * last fortnight is being entered has usually left by the time anybody gets to it — filtering on
   * status would leave their column off the sheet and their wages unentered.
   *
   * Deliberately the same two clauses the server applies when it decides whose sheets a period
   * builds, so this grid and that build hold the same list: a column here that the build leaves out
   * is a day somebody types and nobody is ever paid for. The owner is not on it either way — the
   * members endpoint answers employees only, and a day entered against him is refused outright.
   */
  const people = useMemo(() => {
    if (!period) return [];
    return (staff.data ?? [])
      .filter((member) => {
        const hired = member.hiredAt?.slice(0, 10);
        const left = member.terminatedAt?.slice(0, 10);
        if (hired && hired > period.periodEnd) return false;
        if (left && left < period.periodStart) return false;
        return true;
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [staff.data, period]);

  const recordByKey = useMemo(
    () => new Map((entries.data ?? []).map((entry) => [cellKey(entry.staffId, entry.date), entry])),
    [entries.data],
  );

  /**
   * WHETHER THE DAYS ALREADY ON FILE ARE KNOWN YET — the difference between "there is no record for
   * this day" and "I have not been told about this day".
   *
   * The record query is keyed by the period, so switching period leaves it with no data at all for
   * a whole round trip. Everything that writes into a cell or sends one has to wait for it: during
   * that window an empty map says every day is free, and "Fill the 62 scheduled days" wrote a
   * rostered shift over days that already existed — including paid days off, which the bulk door
   * then replaced, reason and all, because it reuses the row's id.
   */
  const recordsKnown = entries.isSuccess;

  const scheduleHistory = useMemo(() => schedulesByStaff(schedules.data ?? []), [schedules.data]);

  /**
   * The shift somebody's working week expects of one day — the SERVER's own reading of it.
   *
   * `patternDayOn` is the mirror of the rule the timesheet build applies, business-day cutoff and
   * all, and it is imported rather than restated for the reason this file keeps giving: two
   * readings of an effective-dated pattern is how a screen suggests a shift the sheet it feeds then
   * reports as unscheduled. Reading it by calendar weekday instead put a 02:00–06:00 shift on the
   * day it starts by the wall clock rather than the day the business counts it in, and a fill of
   * that lands a day entry the build flags as worked on a day off — four hours of rest-day overtime
   * nobody earned.
   *
   * The roll-over is the shared default rather than this location's own `dayCutoffMinutes`, which
   * no portal hook exposes yet; it is the same value the column defaults to and the same one the
   * build falls back to, so the two agree for this bar today. The day anybody moves the roll-over,
   * this call takes the location's value as its third argument and nothing else changes.
   */
  const scheduledDay = (staffId: string, date: string): PatternDay | undefined =>
    patternDayOn(scheduleHistory.get(staffId) ?? [], date);

  /*
   * Which days may be written as hours alone. Unset — the shipped state, and anything unreadable —
   * means none may, so the mode cannot appear at all; and a period entirely on the clock's side of
   * the date does not offer it either, since there would be no cell it applied to.
   */
  const timesRequiredFrom = policy.data?.timesRequiredFrom ?? null;
  const estimatable = (date: string) => timesRequiredFrom !== null && date < timesRequiredFrom;
  const anyEstimatable = dates.some(estimatable);
  /*
   * The switch as it actually stands on THIS period. Nothing reads `hoursOnly` on its own: a switch
   * left on while the owner clears the date, or while he moves to a fortnight entirely on the
   * clock, would go on choosing a mode for days it no longer applies to — and the banner explaining
   * it would print an em dash where the date should be.
   */
  const hoursHere = hoursOnly && anyEstimatable;

  /** What an EMPTY cell on this day offers. What a typed one is, it decides for itself. */
  const modeOffered = (date: string): CellMode => (hoursHere && estimatable(date) ? 'hours' : 'times');

  /*
   * The one callback that has to keep its identity between renders. Every cell holds it, and a
   * fresh function each render would make `memo(DayCell)` decide that every one of a hundred and
   * fifty cells had changed — which is the whole cost the memo exists to avoid. `changeSheet` is a
   * dispatch and `setRefused` takes the state it updates, so this depends on nothing.
   */
  const setCell = useCallback((key: string, next: Cell) => {
    changeSheet({ how: 'typed', key, cell: next });
    // A refusal stops being the truth about a cell the moment somebody changes it.
    setRefused((current) => (current?.key === key ? null : current));
  }, []);

  /** The cell as it stands, and the ONE reading of it every part of this screen then agrees about. */
  const cellAt = (staffId: string, date: string) => {
    const key = cellKey(staffId, date);
    const typed = sheet[key];
    const record = recordByKey.get(key);
    const offered = modeOffered(date);
    const cell = typed ?? (record ? (cellOfRecord(record, offered) ?? BLANK_CELL) : BLANK_CELL);
    return {
      key,
      cell,
      record,
      typed: typed !== undefined,
      reading: readCell(cell, offered, estimatable(date)),
    };
  };

  /**
   * What one person-day is worth. Typed beats recorded, and a suggestion is worth nothing at all —
   * the running total has to say what the period holds, not what the roster hoped for.
   *
   * Except on a day this sheet may not write, which is worth what is ON FILE however much is typed
   * over it: what is typed there is going nowhere, and a total that counted it would promise hours
   * the save is about to leave behind.
   */
  const minutesAt = (staffId: string, date: string): number | null => {
    const key = cellKey(staffId, date);
    const record = recordByKey.get(key);
    const typed = sheet[key];
    if (typed !== undefined && sheetMayWrite(record)) {
      return readCell(typed, modeOffered(date), estimatable(date)).minutes;
    }
    return record ? recordedMinutes(record) : null;
  };

  const personTotal = (staffId: string) =>
    dates.reduce((sum, date) => sum + (minutesAt(staffId, date) ?? 0), 0);
  const personDays = (staffId: string) =>
    dates.filter(
      (date) => sheet[cellKey(staffId, date)] !== undefined || recordByKey.has(cellKey(staffId, date)),
    ).length;

  /**
   * ONE WALK OF THE SHEET, and everything the buttons and the warnings below say comes out of it.
   *
   * Deliberately UNMEMOIZED: it reads functions defined in this component, so a dependency list
   * would have to name them, and naming a function rebuilt each render is a memo that never holds
   * while looking like one. A month for five people is a hundred and fifty cells and this is one
   * pass of arithmetic over them — nothing beside the render itself. What genuinely costs is
   * re-rendering the cells, and that is what `memo(DayCell)` and the stable `setCell` are for.
   *
   * One walk rather than four, because the four had drifted apart: the count on the button, the
   * "will not be saved" warning and the list actually sent disagreed about which cells they meant,
   * and every one of those disagreements was a typed day that showed on screen and reached nobody.
   *
   * `pending` is IN THE ORDER IT GOES ON THE WIRE. The order is load-bearing: a wholesale refusal
   * names the entry by its position in the array that was sent, and that position is read back
   * against the list that went, not against this one.
   */
  const walk = (() => {
    const pending: PendingDay[] = [];
    /** Typed, and refused as it stands — half a shift, or hours on a day that is on the clock. */
    let faulty = 0;
    /** Typed on a day this sheet may not write, so left exactly where it is. */
    let onDaysOff = 0;
    /** Hours with no times behind them, typed now or recorded as estimates already. */
    let withoutTimes = 0;
    /** Typed cells belonging to this period, as opposed to a draft still holding another one. */
    let typedHere = 0;
    for (const person of people) {
      for (const date of dates) {
        const key = cellKey(person.id, date);
        const record = recordByKey.get(key);
        const typed = sheet[key];
        if (typed === undefined) {
          if (record?.totalsOnly) withoutTimes += 1;
          continue;
        }
        typedHere += 1;
        if (!sheetMayWrite(record)) {
          onDaysOff += 1;
          continue;
        }
        const reading = readCell(typed, modeOffered(date), estimatable(date));
        if (reading.mode === 'hours' && reading.minutes !== null) withoutTimes += 1;
        const entry = entryOf(person.id, date, typed, reading);
        if (entry === null) {
          if (!reading.blank) faulty += 1;
          continue;
        }
        // Nothing goes on the wire until the days already on file are known, for the reason
        // `recordsKnown` gives: an absent record and an unread one are not the same fact.
        if (recordsKnown) pending.push({ entry, key, who: person.displayName, date });
      }
    }
    return { pending, faulty, onDaysOff, withoutTimes, typedHere };
  })();
  const { pending, faulty, onDaysOff, withoutTimes, typedHere } = walk;
  /** Typed days held for another period — invisible here, and still the owner's work. */
  const typedElsewhere = Object.keys(sheet).length - typedHere;

  /**
   * ENTERED, BUT NOT IN THE FIGURES YET — derived, never remembered.
   *
   * A day reaches the timesheets when they are next built and not before, so a sheet saved and left
   * is a period whose figures are quietly behind its record. A flag set at save time would be lost
   * the moment somebody changed tab, and would say nothing about a day entered from the approval
   * screen — so this asks the built sheets instead: a day whose entry has no row on the last build,
   * or a row whose worked minutes are not what the entry says, has not reached the figures. PTO with
   * no minutes is left out of the comparison, because what it is worth is the schedule's answer and
   * only the build can give it.
   *
   * The days behind are then split by whether a rebuild could still fix them, because for one of
   * the two it never can: a rebuild leaves an APPROVED sheet exactly as it is — that is the whole
   * point of approving — so days entered against one are not late, they are locked out, and
   * pressing "Work the hours out" all afternoon changes nothing. Those are named, with the door
   * that does fix them, rather than counted into a nag that could never clear.
   */
  const behindTheFigures = useMemo(() => {
    const nothing = { late: 0, locked: [] as { name: string; days: number }[] };
    if (!built.data || !entries.data) return nothing;
    const byStaff = new Map(built.data.map((row) => [row.staffId, row]));
    let late = 0;
    const locked = new Map<string, { name: string; days: number }>();
    for (const entry of entries.data) {
      const sheetFor = byStaff.get(entry.staffId);
      const day = (sheetFor?.days ?? []).find((row) => row.date === entry.date);
      const minutes = recordedMinutes(entry);
      if (day && (minutes === null || day.workedMinutes === minutes)) continue;
      if (sheetFor?.status === 'approved') {
        const held = locked.get(entry.staffId);
        if (held) held.days += 1;
        else locked.set(entry.staffId, { name: sheetFor.staffName ?? 'somebody', days: 1 });
        continue;
      }
      late += 1;
    }
    return { late, locked: [...locked.values()] };
  }, [built.data, entries.data]);
  const lockedDays = behindTheFigures.locked.reduce((sum, person) => sum + person.days, 0);
  const lockedNames = behindTheFigures.locked.map((person) => person.name).join(', ');

  /**
   * The save carries the ROWS, not just their entries, and reads them back off its own variables
   * afterwards.
   *
   * Both halves of that are the fix to one defect. What came back from a successful save used to
   * reset the whole sheet while only the rows that read as days had gone — so a fortnight typed
   * with one clock-out reading "18:0", or a cell on a day off, or a cell belonging to the period
   * the picker had just been moved off, was erased by the save that never carried it, with no
   * warning and no trace. And a refusal was read against the CURRENT pending list, so anything
   * typed while the request was in flight moved the positions and the server's answer marked the
   * wrong cell.
   */
  const save = useMutation({
    mutationFn: (rows: PendingDay[]) =>
      rawApi<{ entries: TimesheetDayEntry[] }>('PUT', DAY_ENTRIES_BULK_PATH, {
        entries: rows.map((row) => row.entry),
      }),
    onSuccess: (result, sent) => {
      // Exactly what went on the wire leaves the sheet, and nothing else. Everything still typed —
      // a half-finished cell, a day the sheet may not write, another fortnight's column — is work
      // the owner has done and has not been asked about.
      changeSheet({ how: 'saved', keys: sent.map((row) => row.key) });
      setRefused(null);
      setLastSaved(result.entries.length);
      void queryClient.invalidateQueries({ queryKey: ['list', DAY_ENTRIES_PATH] });
      notifyDone(
        `${result.entries.length} ${result.entries.length === 1 ? 'day' : 'days'} recorded`,
        'Work the hours out to bring them into the figures.',
      );
    },
    onError: (err, sent) => {
      /*
       * NOTHING TYPED IS THROWN AWAY. The save is all or nothing, so a refusal means the server
       * wrote none of it — and the sheet on screen is the only copy of an afternoon's work. All
       * that changes is that the cell the server named is marked, in its own words.
       */
      const named = refusedEntry(err);
      const row = named ? sent[named.index] : undefined;
      const { message } = errorCopy(err, 'save these hours');
      if (row) setRefused({ key: row.key, who: row.who, date: row.date, copy: message });
      notifyError(err, 'save these hours');
    },
  });

  const build = useMutation({
    mutationFn: () => rawApi<BuildResult>('POST', `${PERIODS_PATH}/${periodId}/timesheets`, {}),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: listKey(TIMESHEETS_PATH, { payrollPeriodId: periodId ?? '' }),
      });
      // `lastSaved` is deliberately left standing: with the figures caught up it is what turns the
      // yellow "not in the figures yet" line into the green one that says the job is done and where
      // to go next. Clearing it here would leave the sheet saying nothing at all after a rebuild.
      const leftAlone = result.preserved ?? [];
      if (leftAlone.length === 0) {
        notifyDone('Hours worked out', 'The days entered here are in the figures now.');
        return;
      }
      /*
       * The rebuild answers WHO it left alone, and saying "the days entered here are in the figures
       * now" over the top of that is how backfilled hours came to be paid to nobody while the
       * screen reported success. Named, because "some sheets were preserved" is not something an
       * owner can act on and a name is.
       */
      const names = leftAlone.map((row) => row.staffName).join(', ');
      notifyDone(
        leftAlone.length === 1
          ? `${names}’s hours were left as they were`
          : 'Some hours were left as they were',
        `${names} ${leftAlone.length === 1 ? 'has' : 'have'} approved hours for this period, so nothing entered here has reached ${
          leftAlone.length === 1 ? 'that sheet' : 'those sheets'
        }. Reopen the hours under Approve hours, then work them out again.`,
      );
    },
    onError: (err) => notifyError(err, 'work out the hours'),
  });

  /**
   * Writes a suggestion into cells the owner has not answered for.
   *
   * It never writes over a cell somebody typed, and never over a day already on file — a fill is
   * for the days nobody has said anything about, and quietly restating a recorded Tuesday as the
   * rostered one is how a correction gets undone by a convenience. And it does nothing at all until
   * the days on file are KNOWN: a fill against an empty record map is not a fill of the unanswered
   * days, it is a fill of every day in the period, over the top of whatever is already there.
   */
  const fill = (
    targets: { staffId: string; date: string }[],
    shiftFor: (staffId: string, date: string) => Cell | null,
  ) => {
    if (!recordsKnown) return;
    const cells: Sheet = {};
    for (const { staffId, date } of targets) {
      const key = cellKey(staffId, date);
      if (sheet[key] !== undefined || recordByKey.has(key)) continue;
      const cell = shiftFor(staffId, date);
      if (cell) cells[key] = cell;
    }
    changeSheet({ how: 'filled', cells });
  };

  /** The working week's shift for a day, which is what every fill on this screen writes. */
  const rosteredShift = (staffId: string, date: string): Cell | null => {
    const day = scheduledDay(staffId, date);
    return day ? cellOfSchedule(day) : null;
  };

  /** A person's rostered days inside this period, which is what a column fill covers. */
  const rosteredDays = (staffId: string) => dates.filter((date) => scheduledDay(staffId, date));

  /** The first shift in a column or a row, for repeating it across the rest of one. */
  const shiftIn = (cells: { staffId: string; date: string }[]): Cell | null => {
    for (const { staffId, date } of cells) {
      const { cell, typed, record } = cellAt(staffId, date);
      if (!typed && !record) continue;
      const timed = record && !record.totalsOnly ? cellOfRecord(record, 'times') : typed ? cell : null;
      if (timed && cellMinutes(timed, 'times') !== null) return timed;
    }
    return null;
  };

  /** Rostered days nobody has answered for yet — what the one-click fill would write. */
  const suggestedEverywhere = (() => {
    if (!recordsKnown) return 0;
    let count = 0;
    for (const person of people) {
      for (const date of rosteredDays(person.id)) {
        const key = cellKey(person.id, date);
        if (sheet[key] === undefined && !recordByKey.has(key)) count += 1;
      }
    }
    return count;
  })();

  /*
   * Nobody has a week in force over these dates — which is a fact about the roster, not about a
   * request still in flight. Said only once the schedules have actually arrived, so a slow
   * connection does not flash an alarm about working weeks that are perfectly well set.
   */
  const nobodyRostered =
    !schedules.isLoading &&
    people.length > 0 &&
    people.every((person) => rosteredDays(person.id).length === 0);

  /*
   * One save carries a bounded number of days, so a period bigger than the cap goes in two presses
   * rather than being cut down to fit. That is only honest because a save now prunes what it sent
   * and nothing else: the days held back are still on the sheet, still typed, still counted, and
   * the next press takes them.
   */
  const sending = pending.slice(0, MOST_DAYS_IN_ONE_SAVE);
  const heldBack = pending.length - sending.length;

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" align="flex-start" mb="md">
        <div>
          <Title order={5}>Enter hours for the whole period</Title>
          <Text size="sm" c="dimmed">
            Every person, every day, in one sheet. Type a clock-in and a clock-out and move on; leave a day
            blank if nothing was worked or you do not know yet. Nothing is recorded until you save.
          </Text>
        </div>
        <Group gap="xs" wrap="nowrap">
          <Select
            aria-label="Pay period"
            data={(periods.data ?? []).map((p) => ({
              value: p.id,
              label: `${formatDate(p.periodStart)} – ${formatDate(p.periodEnd)}`,
            }))}
            value={periodId}
            onChange={setAskedPeriod}
            placeholder="Choose a pay period"
            style={{ flex: '1 1 180px', maxWidth: 260 }}
          />
        </Group>
      </Group>

      {!period ? (
        <Text size="sm" c="dimmed" fs="italic">
          {periods.isLoading ? 'Reading the pay periods…' : 'Add a pay period first, under Pay periods.'}
        </Text>
      ) : staff.isError || entries.isError || schedules.isError ? (
        <Alert color="red" title="Could not load this period">
          Nothing has been lost — this is only a display problem, and anything you have typed is still on the
          sheet. Try again in a moment.
        </Alert>
      ) : people.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          {staff.isLoading ? 'Reading the team…' : 'Nobody was employed here in this period.'}
        </Text>
      ) : (
        <>
          {/* Typed days outlive a tab switch and a reload, so the first thing to say on the way back
              in is that they are still here — otherwise a screen full of green figures nobody
              remembers typing reads as the software having invented them. */}
          {carriedOver && typedHere + typedElsewhere > 0 && (
            <Alert color="gray" variant="light" mb="md">
              <Text size="sm">
                Picked up where you left off: {typedHere + typedElsewhere}{' '}
                {typedHere + typedElsewhere === 1 ? 'day is' : 'days are'} typed here and not yet recorded.
              </Text>
            </Alert>
          )}

          {refused && (
            <Alert color="red" variant="light" mb="md" title="Nothing was saved">
              <Text size="sm">
                The whole sheet was refused over {refused.who}’s {formatDate(refused.date)}, and it is marked
                below. {refused.copy} Everything you typed is still here.
              </Text>
            </Alert>
          )}

          {/* Entered days only reach the figures on a rebuild, so the state says so rather than
              letting the approval screen quietly disagree with this one. Derived from the built
              sheets, so it is still true after a tab switch and true about days entered elsewhere. */}
          {behindTheFigures.late > 0 && (
            <Alert color="yellow" variant="light" mb="md">
              <Group justify="space-between" wrap="nowrap">
                <Text size="sm">
                  {behindTheFigures.late} {behindTheFigures.late === 1 ? 'day is' : 'days are'} recorded but
                  not in the figures yet. Nobody is paid from this sheet until the hours are worked out.
                </Text>
                <Button size="compact-sm" loading={build.isPending} onClick={() => build.mutate()}>
                  Work the hours out
                </Button>
              </Group>
            </Alert>
          )}

          {/* The days a rebuild can never carry. Working the hours out leaves an approved sheet
              exactly as it is, so this is not a nag that another press would clear — it names who
              has to be reopened before these days are worth anything to anybody. */}
          {behindTheFigures.locked.length > 0 && (
            <Alert color="orange" variant="light" mb="md" title="Already approved — these days reach nobody">
              <Text size="sm">
                {lockedDays} {lockedDays === 1 ? 'day' : 'days'} entered for {lockedNames}{' '}
                {lockedDays === 1 ? 'sits' : 'sit'} outside the figures, because{' '}
                {behindTheFigures.locked.length === 1
                  ? 'their sheet for this period is'
                  : 'their sheets for this period are'}{' '}
                already approved. Working the hours out leaves an approved sheet as it is, so{' '}
                {lockedDays === 1 ? 'that day' : 'those days'} will not be paid until the hours are reopened.
              </Text>
              <Button component={Link} to={APPROVE_HOURS_TAB} size="compact-sm" mt="xs">
                Reopen the hours
              </Button>
            </Alert>
          )}

          {lastSaved !== null && behindTheFigures.late === 0 && behindTheFigures.locked.length === 0 && (
            <Alert color="teal" variant="light" mb="md">
              <Text size="sm">
                {lastSaved} {lastSaved === 1 ? 'day is' : 'days are'} recorded and in the figures.{' '}
                <Anchor component={Link} to={APPROVE_HOURS_TAB}>
                  Approve the hours
                </Anchor>{' '}
                when you are ready.
              </Text>
            </Alert>
          )}

          {nobodyRostered && (
            <Alert color="yellow" variant="light" mb="md" title="No working weeks are set">
              <Text size="sm">
                Nobody has a working week in force over these dates, so no day can be suggested and every
                shift has to be typed. Setting them fills the sheet in one click, and it is also what makes
                overtime and a short day mean anything.
              </Text>
              <Button component={Link} to={WORKING_WEEKS_TAB} size="compact-sm" mt="xs">
                Set the working weeks
              </Button>
            </Alert>
          )}

          <Group justify="space-between" align="flex-end" mb="sm" wrap="wrap">
            <Group gap="xs">
              <Button
                variant="light"
                size="compact-sm"
                disabled={suggestedEverywhere === 0}
                onClick={() =>
                  fill(
                    people.flatMap((person) =>
                      rosteredDays(person.id).map((date) => ({ staffId: person.id, date })),
                    ),
                    rosteredShift,
                  )
                }
              >
                {!recordsKnown
                  ? 'Reading the days already recorded…'
                  : suggestedEverywhere > 0
                    ? `Fill the ${suggestedEverywhere} scheduled ${suggestedEverywhere === 1 ? 'day' : 'days'}`
                    : 'Every scheduled day is answered'}
              </Button>
              {typedHere > 0 && (
                <Button
                  variant="subtle"
                  size="compact-sm"
                  color="red"
                  // The one gesture that throws typed work away, so it counts what it is about to
                  // throw away rather than saying "what I typed" — this is the only way off the
                  // sheet that is not a save, and it now stands beside a draft that has survived a
                  // tab switch and may be larger than the last thing the owner remembers doing.
                  // Scoped to the period on screen: the sheet can be holding another fortnight's
                  // typing, and this is a statement about what is in front of him.
                  onClick={() =>
                    changeSheet({
                      how: 'discarded',
                      keys: people.flatMap((person) => dates.map((date) => cellKey(person.id, date))),
                    })
                  }
                >
                  Clear the {typedHere} {typedHere === 1 ? 'day' : 'days'} I typed
                </Button>
              )}
            </Group>

            {/* The estimate mode appears only where the owner has already said it may — and where
                there is at least one day in this period it could apply to. */}
            {anyEstimatable ? (
              <Switch
                checked={hoursOnly}
                onChange={(e) => setHoursOnly(e.currentTarget.checked)}
                label="Enter hours, not times"
                description={`For days before ${formatDate(timesRequiredFrom)}, when the times are gone`}
              />
            ) : (
              <Anchor component="button" type="button" size="sm" onClick={() => setSettingTheDate(true)}>
                {timesRequiredFrom
                  ? `Every day here is on the clock — times required from ${formatDate(timesRequiredFrom)}`
                  : 'Backfilling months nobody punched?'}
              </Anchor>
            )}
          </Group>

          {hoursHere && (
            <Alert color="gray" variant="light" mb="sm">
              <Text size="sm">
                Days before {formatDate(timesRequiredFrom)} take a bare number of hours — 8, 7.5 or 7:30 — and
                are recorded as estimates. Hours with no times cannot be split into day and night bands later,
                so a night or rest-day premium can never be worked out for them. Put the times in wherever you
                have them; a day you have typed times into keeps them whatever this switch says.{' '}
                <Anchor component="button" type="button" onClick={() => setSettingTheDate(true)}>
                  Change the day the clock took over
                </Anchor>
              </Text>
            </Alert>
          )}

          <Table.ScrollContainer minWidth={Math.max(700, 180 + people.length * 160)}>
            <Table striped withColumnBorders verticalSpacing={4} horizontalSpacing="xs" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ minWidth: 160 }}>Day</Table.Th>
                  {people.map((person) => {
                    const total = personTotal(person.id);
                    const column = dates.map((date) => ({ staffId: person.id, date }));
                    const rostered = rosteredDays(person.id);
                    const repeatable = shiftIn(column);
                    return (
                      <Table.Th key={person.id} style={{ minWidth: 160 }}>
                        <Group gap={4} wrap="nowrap" justify="space-between" align="flex-start">
                          <div>
                            <Anchor component={Link} to={`/staff/${person.id}`} fw={600} size="sm">
                              {person.displayName}
                            </Anchor>
                            {/* The shape of the period as it is typed, where the eye already is. */}
                            <Text size="sm" fw={700} lh={1.2}>
                              {formatHours(total)}
                            </Text>
                            <Text size="xs" c="dimmed" lh={1.2}>
                              {personDays(person.id)} {personDays(person.id) === 1 ? 'day' : 'days'}
                            </Text>
                          </div>
                          <Menu position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                aria-label={`Fill ${person.displayName}'s days`}
                              >
                                ⋯
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                disabled={rostered.length === 0 || !recordsKnown}
                                onClick={() =>
                                  fill(
                                    rostered.map((date) => ({ staffId: person.id, date })),
                                    rosteredShift,
                                  )
                                }
                              >
                                Fill their working week
                              </Menu.Item>
                              <Menu.Item
                                disabled={repeatable === null || !recordsKnown}
                                onClick={() =>
                                  repeatable &&
                                  fill(
                                    (rostered.length > 0 ? rostered : dates).map((date) => ({
                                      staffId: person.id,
                                      date,
                                    })),
                                    () => repeatable,
                                  )
                                }
                              >
                                {repeatable
                                  ? `Repeat ${repeatable.start}–${repeatable.end} on every ${
                                      rostered.length > 0 ? 'scheduled day' : 'day'
                                    }`
                                  : 'Repeat their first shift'}
                              </Menu.Item>
                              <Menu.Item
                                color="red"
                                onClick={() =>
                                  changeSheet({
                                    how: 'discarded',
                                    // This period's cells only. The sheet can be holding another
                                    // fortnight's typing, and "clear this column" is a statement
                                    // about the column on the screen.
                                    keys: dates.map((date) => cellKey(person.id, date)),
                                  })
                                }
                              >
                                Clear this column
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Table.Th>
                    );
                  })}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {dates.map((date) => {
                  const weekday = weekdayOf(date);
                  const row = people.map((person) => ({ staffId: person.id, date }));
                  const repeatable = shiftIn(row);
                  const scheduledHere = row.filter(({ staffId }) => scheduledDay(staffId, date));
                  return (
                    <Table.Tr
                      key={date}
                      // A line where each week starts, so thirty-one rows stay readable as weeks
                      // rather than as one long list of numbers.
                      style={
                        weekday === 0
                          ? { borderTop: '2px solid var(--mantine-color-default-border)' }
                          : undefined
                      }
                    >
                      <Table.Td>
                        <Group gap={6} wrap="nowrap" justify="space-between">
                          <div>
                            <Text size="sm" fw={600} lh={1.2}>
                              {weekday === null ? '—' : DAY_SHORT[weekday]}
                            </Text>
                            <Text size="xs" c="dimmed" lh={1.2}>
                              {formatDate(date)}
                            </Text>
                          </div>
                          <Group gap={4} wrap="nowrap">
                            {estimatable(date) && hoursHere && (
                              <Badge size="xs" variant="light" color="gray">
                                No times
                              </Badge>
                            )}
                            <Menu position="bottom-start" withinPortal>
                              <Menu.Target>
                                <ActionIcon
                                  variant="subtle"
                                  size="sm"
                                  aria-label={`Fill ${formatDate(date)}`}
                                >
                                  ⋯
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  disabled={scheduledHere.length === 0 || !recordsKnown}
                                  onClick={() => fill(scheduledHere, rosteredShift)}
                                >
                                  Fill everyone scheduled
                                </Menu.Item>
                                <Menu.Item
                                  disabled={repeatable === null || !recordsKnown}
                                  onClick={() => repeatable && fill(row, () => repeatable)}
                                >
                                  {repeatable
                                    ? `Repeat ${repeatable.start}–${repeatable.end} for everyone`
                                    : 'Repeat this day’s shift'}
                                </Menu.Item>
                                <Menu.Item
                                  color="red"
                                  onClick={() =>
                                    changeSheet({
                                      how: 'discarded',
                                      keys: people.map((person) => cellKey(person.id, date)),
                                    })
                                  }
                                >
                                  Clear this day
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </Group>
                        </Group>
                      </Table.Td>

                      {people.map((person) => {
                        const { key, cell, reading, typed, record } = cellAt(person.id, date);
                        // A day this sheet may not write is shown and left alone: it has a door of
                        // its own where a reason belongs with it, and it must not be one keystroke
                        // from being lost. Anything typed over one stays typed and stays unsent —
                        // said out loud here, with the only gesture that removes it.
                        if (record && !sheetMayWrite(record)) {
                          return (
                            <Table.Td key={person.id}>
                              <Stack gap={2}>
                                <Text size="xs" c="dimmed" fs="italic">
                                  Paid day off
                                  {record.minutes != null ? ` · ${formatHours(record.minutes)}` : ''}
                                </Text>
                                {record.note ? <NoteMark note={record.note} /> : null}
                                {typed && (
                                  <Text size="xs" c="red.7">
                                    You typed here. This day is left alone —{' '}
                                    <Anchor
                                      component="button"
                                      type="button"
                                      size="xs"
                                      onClick={() => changeSheet({ how: 'discarded', keys: [key] })}
                                    >
                                      discard it
                                    </Anchor>
                                    , or change the day off where it was entered.
                                  </Text>
                                )}
                              </Stack>
                            </Table.Td>
                          );
                        }
                        const scheduled = scheduledDay(person.id, date);
                        const suggest = !typed && !record && scheduled && reading.mode === 'times';
                        return (
                          <Table.Td key={person.id}>
                            <DayCell
                              sheetKey={key}
                              who={person.displayName}
                              when={formatDate(date)}
                              mode={reading.mode}
                              cell={cell}
                              minutes={reading.minutes}
                              fault={reading.fault}
                              typed={typed}
                              onFile={
                                record && !typed
                                  ? record.totalsOnly
                                    ? `On file: ${formatHours(record.minutes ?? 0)}, no times`
                                    : reading.mode === 'hours' && record.startTime && record.endTime
                                      ? `On file: ${describeShift(record.startTime, record.endTime)}`
                                      : null
                                  : null
                              }
                              note={record?.note ?? null}
                              ghostStart={suggest ? scheduled.startTime : ''}
                              ghostEnd={suggest ? scheduled.endTime : ''}
                              refusedAs={refused?.key === key ? refused.copy : null}
                              onChange={setCell}
                            />
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          <Stack gap="xs" mt="md">
            {/* The count the figures cannot show. Eight hours is eight hours on the register whether
                somebody punched them or remembered them, and this line is the only place the period
                says how much of it is remembered. */}
            {withoutTimes > 0 && (
              <Text size="sm" c="dimmed">
                {withoutTimes} {withoutTimes === 1 ? 'day' : 'days'} on this sheet{' '}
                {withoutTimes === 1 ? 'is' : 'are'} entered as hours with no times, recorded as{' '}
                {withoutTimes === 1 ? 'an estimate' : 'estimates'}. Hours with no times can never be split
                into day and night bands.
              </Text>
            )}
            {faulty > 0 && (
              <Text size="sm" c="red.7">
                {faulty} {faulty === 1 ? 'day is' : 'days are'} typed but will not be saved as{' '}
                {faulty === 1 ? 'it stands' : 'they stand'} — each one says why underneath, in red.{' '}
                {faulty === 1 ? 'It stays' : 'They stay'} on the sheet either way.
              </Text>
            )}
            {onDaysOff > 0 && (
              <Text size="sm" c="dimmed">
                {onDaysOff} {onDaysOff === 1 ? 'day you typed is' : 'days you typed are'} on a paid day off,
                which this sheet leaves alone. Discard what you typed there, or change the day off where it
                was entered.
              </Text>
            )}
            {typedElsewhere > 0 && (
              <Text size="sm" c="dimmed">
                {typedElsewhere} typed {typedElsewhere === 1 ? 'day belongs' : 'days belong'} to another pay
                period and {typedElsewhere === 1 ? 'is' : 'are'} not shown here.{' '}
                {typedElsewhere === 1 ? 'It stays' : 'They stay'} on the sheet until{' '}
                {typedElsewhere === 1 ? 'it is' : 'they are'} saved or cleared — choose that period to see{' '}
                {typedElsewhere === 1 ? 'it' : 'them'}.
              </Text>
            )}
            {heldBack > 0 && (
              <Text size="sm">
                One save carries {MOST_DAYS_IN_ONE_SAVE} days and {pending.length} are ready, so this takes
                two goes: {sending.length} go now, and the other {heldBack} stay on the sheet for the next
                press.
              </Text>
            )}
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {!recordsKnown
                  ? 'Reading the days already recorded, so nothing here can be written over one…'
                  : pending.length === 0
                    ? typedHere === 0
                      ? 'Nothing typed yet. A blank day means “nothing recorded”, never zero hours worked.'
                      : 'Nothing on this sheet reads as a day to record yet.'
                    : `${pending.length} ${pending.length === 1 ? 'day' : 'days'} ready to record.`}
              </Text>
              <Button
                loading={save.isPending}
                disabled={sending.length === 0}
                onClick={() => save.mutate(sending)}
              >
                Record {sending.length} {sending.length === 1 ? 'day' : 'days'}
              </Button>
            </Group>
          </Stack>
        </>
      )}

      {settingTheDate && (
        <ClockTookOverModal
          policy={policy.data ?? { timesRequiredFrom: null }}
          onClose={() => setSettingTheDate(false)}
        />
      )}
    </Paper>
  );
}
